import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterDataRadio } from '@/app/common/filters/core/filtersTypes.ts';
import { Radio } from '@lmn/design-system';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { CustomOptionsCreators } from '@/app/common/filters/types/shared/Custom/types.tsx';
import SortRadioComponent from '@/app/common/filters/types/shared/Custom/SortRadioComponent.tsx';
import { Column } from '@/app/common/components/Base/List.tsx';
import { getColumnRows } from '@/app/common/filters/surfaces/columns.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import styled from 'styled-components';
import React from 'react';

const TsxRadio = styled(Radio)<{ $evo?: boolean }>`
  width: 100%;
  padding: ${({ $evo }) => ($evo ? '8px 0' : '12px 0')};

  [class*='RadioLabel'] {
    margin-left: 12px;
  }
`;

export const FilterRadio = ({
  filter,
  columns = 1,
  isLoading,
  handleFilterChanged,
  useStandardRadios,
}: FilterComponentProps & { useStandardRadios?: boolean }) => {
  const { disabled } = filter;
  const { useEvoFilters } = useFiltersUiData();
  const filterData = filter.data as FilterDataRadio;

  const isOptionDisabled = (index: number) => filterData.options[index].disabled || disabled || isLoading;

  const findEnabledIndex = (start: number, step: 1 | -1) => {
    const total = filterData.options.length;
    for (let i = 1; i <= total; i += 1) {
      const next = (start + step * i + total * i) % total;
      if (!isOptionDisabled(next)) return next;
    }
    return start;
  };

  const focusOption = (currentTarget: HTMLElement, index: number) => {
    currentTarget.parentElement?.querySelector<HTMLElement>(`[data-index="${index}"] input`)?.focus();
  };

  const handleFilterClicked = (valueClicked: string) => {
    const updatedOptions = filterData.options.map((option) => {
      if (valueClicked === filterData.defaultValue)
        return { ...option, selected: option.value === filterData.defaultValue };
      if (option.value === filterData.defaultValue) return { ...option, selected: false };
      return { ...option, selected: option.value === valueClicked };
    });
    const updatedFilter = {
      ...filter,
      data: {
        ...filter.data,
        options: updatedOptions,
      },
    };
    handleFilterChanged([updatedFilter]);
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    const isDisabled = isOptionDisabled(index);
    switch (e.key) {
      case 'Enter':
        if (!isDisabled) {
          e.preventDefault();
          handleFilterClicked(filterData.options[index].value);
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        focusOption(e.currentTarget, findEnabledIndex(index, 1));
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        focusOption(e.currentTarget, findEnabledIndex(index, -1));
        break;
      case 'Home':
        e.preventDefault();
        focusOption(e.currentTarget, findEnabledIndex(filterData.options.length - 1, 1));
        break;
      case 'End':
        e.preventDefault();
        focusOption(e.currentTarget, findEnabledIndex(0, -1));
        break;
      default:
        break;
    }
  };

  const customOptionComponents: CustomOptionsCreators = {
    [FILTER_NAME.SORT]: SortRadioComponent,
  };

  const options = filterData.options.map((option, index) => {
    const isDisabled = isOptionDisabled(index);

    const CustomComponent = !useStandardRadios ? customOptionComponents?.[filter.name] : undefined;
    if (CustomComponent) {
      return (
        <li
          role="option"
          aria-selected={option.selected}
          data-index={index}
          key={option.value}
          style={{ listStyle: 'none' }}
          onKeyDown={(e) => handleOptionKeyDown(e, index)}
        >
          <CustomComponent option={option} onChange={() => handleFilterClicked(option.value)} index={index} />
        </li>
      );
    }

    return (
      <li
        role="option"
        aria-selected={option.selected}
        data-index={index}
        key={option.value}
        style={{ listStyle: 'none' }}
        onKeyDown={(e) => handleOptionKeyDown(e, index)}
      >
        <TsxRadio
          $evo={useEvoFilters}
          hoverBackground={false}
          label={<LabelNode option={option} isDisabled={isDisabled} />}
          onChange={() => handleFilterClicked(option.value)}
          checked={option.selected}
          disabled={isDisabled}
        />
      </li>
    );
  });

  return (
    <Column columns={columns} rows={getColumnRows(filter.name, filterData.options.length, columns)} gap={0} as="ul">
      {options}
    </Column>
  );
};
